import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  Sparkles, Upload, Loader2, Check, X, ArrowRight, Image as ImageIcon,
} from "lucide-react";
import { useFeatures } from "../../context/FeaturesContext";

/**
 * Smart Auto-Catalog wizard.
 *   1. Admin drag-drops 1-30 flat-lay images
 *   2. Backend analyzes each with Gemini → returns title/desc/category/etc.
 *   3. Admin reviews + tweaks each card inline
 *   4. Click "Create products" — bulk-creates them and navigates to /admin/products
 */
export default function AdminSmartCatalog() {
  const { has, loading: featLoading } = useFeatures();
  const enabled = has("ai_smart_catalog");
  const [files, setFiles] = useState([]);
  const [job, setJob] = useState(null);
  const [items, setItems] = useState([]);     // editable per-product cards
  const [submitting, setSubmitting] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [keep, setKeep] = useState({});       // {sourceUrl: true}
  const navigate = useNavigate();

  // Poll the running job
  useEffect(() => {
    if (!job?.job_id || job?.status === "done") return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/admin/smart-catalog/jobs/${job.job_id}`);
        if (cancelled) return;
        setJob(data);
        if (data.status === "running") setTimeout(tick, 3000);
        else if (data.status === "done") {
          // Pull each item's attributes into editable cards
          const cards = (data.items || []).map((it) => ({
            source_url: it.source_url,
            status: it.status,
            error: it.error,
            ...(it.attributes || {}),
            price: it.attributes?.suggested_price_inr ?? 0,
            mrp: it.attributes?.suggested_mrp_inr ?? 0,
            tags: it.attributes?.tags ?? [],
          }));
          setItems(cards);
          const initialKeep = {};
          cards.forEach((c) => { if (c.status === "done") initialKeep[c.source_url] = true; });
          setKeep(initialKeep);
          toast.success("Analysis complete — review and create products");
        }
      } catch {
        if (!cancelled) toast.error("Lost connection to the analysis job");
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [job?.job_id, job?.status]);

  const startJob = async () => {
    if (files.length === 0) return toast.error("Pick at least one image");
    setSubmitting(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const { data } = await api.post("/admin/smart-catalog/jobs", fd);
      setJob({ job_id: data.job_id, status: "running", items: [] });
      toast.success(`Analyzing ${data.total_images} image(s)…`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start job");
    } finally {
      setSubmitting(false);
    }
  };

  const updateItem = (sourceUrl, patch) => {
    setItems((xs) => xs.map((c) => c.source_url === sourceUrl ? { ...c, ...patch } : c));
  };

  const commit = async () => {
    const picked = items.filter((c) => c.status === "done" && keep[c.source_url]);
    if (picked.length === 0) return toast.error("Tick at least one product to create");
    const missingTitle = picked.find((c) => !c.title?.trim());
    if (missingTitle) return toast.error("All picked products need a title");
    setCommitting(true);
    try {
      const { data } = await api.post(`/admin/smart-catalog/jobs/${job.job_id}/commit`, {
        items: picked.map((c) => ({
          source_url: c.source_url,
          title: c.title,
          description: c.description || "",
          category_slug: c.category_slug,
          color: c.color || "",
          fabric: c.fabric || "",
          occasion: c.occasion || "",
          tags: c.tags || [],
          price: parseFloat(c.price) || 0,
          mrp: parseFloat(c.mrp) || 0,
          status: "draft",
        })),
      });
      toast.success(`Created ${data.created} product(s)`);
      navigate("/admin/products");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't create products");
    } finally {
      setCommitting(false);
    }
  };

  if (featLoading) return <div className="h-32 bg-[hsl(var(--kb-surface-alt))] animate-pulse" />;

  if (!enabled) {
    return (
      <div className="bg-white border border-dashed border-[hsl(var(--kb-border))] p-10 text-center" data-testid="smart-catalog-locked">
        <Sparkles size={28} className="mx-auto mb-3 text-[hsl(var(--kb-muted))]" />
        <h1 className="font-display text-2xl mb-2">Smart Auto-Catalog</h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] max-w-md mx-auto mb-4">
          Drag-drop flat-lay photos and let AI write your titles, descriptions, categories,
          fabric, occasion, tags, and suggested prices. Reduces 200-product onboarding from days to minutes.
        </p>
        <a href="/admin/billing" className="kb-btn-primary inline-flex items-center gap-2" data-testid="smart-catalog-upgrade">
          Enable in Billing
        </a>
      </div>
    );
  }

  const done = job?.completed_count || 0;
  const total = job?.items?.length || files.length;

  return (
    <div data-testid="smart-catalog-page" className="space-y-6 max-w-6xl">
      <div>
        <div className="overline mb-1">Catalog</div>
        <h1 className="font-display text-3xl md:text-4xl flex items-center gap-2">
          <Sparkles size={26} /> Smart Auto-Catalog
        </h1>
        <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-2xl">
          Drag-drop flat-lay photos. Our AI fills in title, description, category, color, fabric,
          occasion, tags and suggested pricing. Review, tweak, and click <em>Create products</em>.
        </p>
      </div>

      {/* STEP 1: upload */}
      {!job && (
        <section className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="smart-catalog-uploader">
          <label
            className="block border-2 border-dashed border-[hsl(var(--kb-border))] p-10 text-center cursor-pointer hover:border-[hsl(var(--kb-primary))]/50 transition"
            data-testid="smart-catalog-dropzone"
          >
            <Upload size={28} className="mx-auto mb-2 text-[hsl(var(--kb-muted))]" />
            <div className="font-medium mb-1">Drop product photos here</div>
            <div className="text-xs text-[hsl(var(--kb-muted))]">.jpg / .png · up to 30 images per batch</div>
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              data-testid="smart-catalog-file-input"
            />
          </label>

          {files.length > 0 && (
            <>
              <div className="text-sm mt-4" data-testid="smart-catalog-file-count">
                {files.length} image(s) selected.
              </div>
              <div className="mt-3 flex gap-2 overflow-x-auto pb-2">
                {files.map((f, i) => (
                  <div key={i} className="w-20 aspect-square border border-[hsl(var(--kb-border))] overflow-hidden flex-shrink-0">
                    <img src={URL.createObjectURL(f)} alt="" className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button type="button" onClick={() => setFiles([])} className="kb-btn-outline" data-testid="smart-catalog-clear-files">
                  Clear
                </button>
                <button
                  type="button"
                  onClick={startJob}
                  disabled={submitting}
                  className="kb-btn-primary inline-flex items-center gap-2"
                  data-testid="smart-catalog-analyze-btn"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Analyze with AI
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* STEP 2: progress while running */}
      {job?.status === "running" && (
        <section className="bg-white border border-[hsl(var(--kb-border))] p-6" data-testid="smart-catalog-progress">
          <div className="flex items-center gap-2 mb-3">
            <Loader2 size={18} className="animate-spin" />
            <div className="font-medium">Analyzing {done} of {total} image(s)…</div>
          </div>
          <div className="bg-[hsl(var(--kb-surface-alt))] h-2 w-full">
            <div className="bg-[hsl(var(--kb-primary))] h-2 transition-all" style={{ width: `${total ? (done / total) * 100 : 0}%` }} />
          </div>
          <p className="text-xs text-[hsl(var(--kb-muted))] mt-2">
            You can leave this page — we'll send you an in-app notification when it's done.
          </p>
        </section>
      )}

      {/* STEP 3: review + commit */}
      {job?.status === "done" && items.length > 0 && (
        <>
          <div className="flex items-center justify-between gap-3 bg-white border border-[hsl(var(--kb-border))] p-3">
            <div className="text-sm">
              <span className="font-medium">{Object.values(keep).filter(Boolean).length}</span> selected ·
              {items.filter((c) => c.status === "done").length} ready · {items.filter((c) => c.status === "failed").length} failed
            </div>
            <button
              type="button"
              onClick={commit}
              disabled={committing}
              className="kb-btn-primary inline-flex items-center gap-2"
              data-testid="smart-catalog-commit-btn"
            >
              {committing ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
              Create products <ArrowRight size={14} />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4" data-testid="smart-catalog-card-grid">
            {items.map((c) => (
              <ProductCard
                key={c.source_url}
                card={c}
                allowedSlugs={job?.allowed_slugs || []}
                kept={!!keep[c.source_url]}
                onToggleKeep={() => setKeep((k) => ({ ...k, [c.source_url]: !k[c.source_url] }))}
                onChange={(patch) => updateItem(c.source_url, patch)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ProductCard({ card, allowedSlugs, kept, onToggleKeep, onChange }) {
  const failed = card.status === "failed";
  return (
    <section
      className={`bg-white border-2 ${kept ? "border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))]"} ${failed ? "opacity-60" : ""}`}
      data-testid={`smart-catalog-card-${card.source_url.slice(-12)}`}
    >
      <div className="flex">
        <div className="w-32 flex-shrink-0 aspect-square bg-[hsl(var(--kb-surface-alt))]">
          {card.source_url ? (
            <img
              src={card.source_url.startsWith("http") ? card.source_url : `${process.env.REACT_APP_BACKEND_URL}${card.source_url}`}
              alt=""
              className="w-full h-full object-cover"
            />
          ) : <ImageIcon size={24} className="m-auto" />}
        </div>
        <div className="flex-1 p-3 space-y-2 text-xs">
          {failed ? (
            <div className="text-red-700">Analysis failed: {card.error || "no data"}</div>
          ) : (
            <>
              <input
                className="kb-input"
                value={card.title || ""}
                onChange={(e) => onChange({ title: e.target.value })}
                placeholder="Product title"
                data-testid="card-title"
              />
              <textarea
                className="kb-input"
                rows={2}
                value={card.description || ""}
                onChange={(e) => onChange({ description: e.target.value })}
                placeholder="Description"
                data-testid="card-description"
              />
              <div className="grid grid-cols-2 gap-2">
                <select
                  className="kb-input"
                  value={card.category_slug || ""}
                  onChange={(e) => onChange({ category_slug: e.target.value })}
                  data-testid="card-category"
                >
                  <option value="">Category…</option>
                  {allowedSlugs.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input className="kb-input" placeholder="Color" value={card.color || ""} onChange={(e) => onChange({ color: e.target.value })} />
                <input className="kb-input" placeholder="Fabric" value={card.fabric || ""} onChange={(e) => onChange({ fabric: e.target.value })} />
                <input className="kb-input" placeholder="Occasion" value={card.occasion || ""} onChange={(e) => onChange({ occasion: e.target.value })} />
                <input
                  className="kb-input"
                  type="number"
                  placeholder="Price (INR)"
                  value={card.price ?? ""}
                  onChange={(e) => onChange({ price: e.target.value })}
                  data-testid="card-price"
                />
                <input
                  className="kb-input"
                  type="number"
                  placeholder="MRP (INR)"
                  value={card.mrp ?? ""}
                  onChange={(e) => onChange({ mrp: e.target.value })}
                />
              </div>
            </>
          )}
          <button
            type="button"
            onClick={onToggleKeep}
            disabled={failed}
            className={`w-full text-xs py-1.5 border ${kept ? "bg-[hsl(var(--kb-primary))] text-white border-[hsl(var(--kb-primary))]" : "border-[hsl(var(--kb-border))]"}`}
            data-testid="card-keep-toggle"
          >
            {kept ? <span className="inline-flex items-center justify-center gap-1"><Check size={12} /> Will create</span>
                  : <span className="inline-flex items-center justify-center gap-1"><X size={12} /> Skip</span>}
          </button>
        </div>
      </div>
    </section>
  );
}
